import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { LruMap } from '@/lib/lru';
import type { LinkPreview } from '@bunker/shared';

// Process-lifetime cache so re-rendering a busy channel doesn't refetch, and two
// messages sharing a link only hit the server once. `null` = tried, no preview.
// Bounded (LRU) so a long session scrolling link-heavy history can't grow the
// map without limit — old previews fall out once the cap is reached.
const cache = new LruMap<string, LinkPreview | null>(300);
const inflight = new Map<string, Promise<LinkPreview | null>>();

// Cache key folds in the channel: the EMBED_LINKS gate is per-channel, so the
// same URL may unfurl in one channel and be suppressed in another. Keying by
// channel keeps a "denied → null" result from leaking to other channels.
function keyOf(url: string, channelId?: string): string {
  return `${channelId ?? ''}|${url}`;
}

function load(url: string, channelId?: string): Promise<LinkPreview | null> {
  const key = keyOf(url, channelId);
  if (cache.has(key)) return Promise.resolve(cache.get(key) ?? null);
  const existing = inflight.get(key);
  if (existing) return existing;
  const p = api
    .unfurl(url, channelId)
    .then((d) => d as LinkPreview | null)
    .catch(() => null)
    .then((d) => {
      cache.set(key, d);
      inflight.delete(key);
      return d;
    });
  inflight.set(key, p);
  return p;
}

/**
 * Resolve a link's read-later preview card via the server (cached, SSRF-guarded
 * endpoint). Returns null while loading, when the link can't be unfurled, or
 * when the caller lacks EMBED_LINKS in `channelId` — a bare link just stays a
 * bare (linkified) link.
 */
export function useLinkPreview(url: string | null, channelId?: string): LinkPreview | null {
  const [data, setData] = useState<LinkPreview | null>(() =>
    url ? (cache.get(keyOf(url, channelId)) ?? null) : null,
  );
  useEffect(() => {
    if (!url) return;
    let alive = true;
    void load(url, channelId).then((d) => {
      if (alive) setData(d);
    });
    return () => {
      alive = false;
    };
  }, [url, channelId]);
  return url ? data : null;
}
